import { ChangeDetectionStrategy, Component, inject, input, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MultiSelectModule } from 'primeng/multiselect';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { MenuModule } from 'primeng/menu';
import { InputTextModule } from 'primeng/inputtext';
import { Select } from 'primeng/select';
import { InputNumber } from 'primeng/inputnumber';
import { Tooltip } from 'primeng/tooltip';
import { ArgDef, ArgEntry, ConfigMeta, UsageResult, VarDef } from '../../models';
import { EditorConfigService } from '../../../features/editor/services/editor-config.service';
import { emptyValueForArgType } from '../../utils/arg-value';
import { argKeyError, findExistingKey, isLegacyArgKey, isMalformedArgKey, normalizeArgKey } from '../../utils/arg-key';

type DefDialogMode = 'create-arg' | 'edit-arg' | 'create-var' | 'edit-var';

@Component({
	selector: 'app-config-panel',
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [FormsModule, MultiSelectModule, DialogModule, ButtonModule, MenuModule, InputTextModule, Select, InputNumber, Tooltip],
	templateUrl: './config-panel.html',
	styleUrl: './config-panel.scss',
})
export class ConfigPanelComponent {
	private configService = inject(EditorConfigService);

	meta = input<ConfigMeta | null>(null);
	chapters = input<string[]>([]);
	argDefs = input<ArgDef[]>([]);
	varDefs = input<VarDef[]>([]);
	args = input<ArgEntry[]>([]);
	vars = input<ArgEntry[]>([]);
	region = input('AWS');
	saved = input(false);

	regionChange = output<string>();
	chaptersChange = output<string[]>();
	argChange = output<{ index: number; value: string }>();
	varChange = output<{ index: number; value: string }>();

	// ── Tab state ───────────────────────────────────────────────────────
	activeTab = signal<'values' | 'defs'>('values');

	// ── Def dialog state ────────────────────────────────────────────────
	defDialogVisible = signal(false);
	defDialogMode = signal<DefDialogMode>('create-arg');
	defDialogKey = signal('');
	defDialogType = signal<string>('string');

	/** Switching the type resets the default — see attributes-editor (DEV-749). */
	onDefDialogTypeChange(type: string): void {
		this.defDialogType.set(type);
		this.defDialogDefaultValue.set(emptyValueForArgType(type as ArgDef['type']));
	}
	defDialogOptions = signal('');
	defDialogDefaultValue = signal<string | number | boolean>('');
	readonly booleanOptions = [
		{ label: 'true', value: true },
		{ label: 'false', value: false },
	];
	defDialogDescription = signal('');
	defDialogEditingKey = signal<string | null>(null);

	// ── Delete dialog state ─────────────────────────────────────────────
	deleteDialogVisible = signal(false);
	deleteDialogKey = signal('');
	deleteDialogKind = signal<'arg' | 'var'>('arg');
	deleteDialogLoading = signal(false);
	deleteDialogUsage = signal<UsageResult | null>(null);

	get defDialogTitle(): string {
		switch (this.defDialogMode()) {
			case 'create-arg': return 'New Argument';
			case 'edit-arg': return 'Edit Argument';
			case 'create-var': return 'New Variable';
			case 'edit-var': return 'Edit Variable';
		}
	}

	get isArgDialog(): boolean {
		return this.defDialogMode() === 'create-arg' || this.defDialogMode() === 'edit-arg';
	}

	get isCreateDialog(): boolean {
		return this.defDialogMode() === 'create-arg' || this.defDialogMode() === 'create-var';
	}

	/**
	 * DEV-651: badge a stored key only when it is genuinely unusable — a space
	 * or a character with no business in an identifier. Merely being
	 * SCREAMING_SNAKE is not flagged here: every key in every live project looks
	 * like that, so a badge on all of them would be noise rather than a signal.
	 * The "this predates the naming rule" note is shown in the edit dialog
	 * instead, where it is in front of someone who asked about that one key.
	 */
	isMalformedKey(key: string): boolean {
		return isMalformedArgKey(key);
	}

	/** The keys the typed one could collide with — args or vars, per dialog mode. */
	private get siblingKeys(): string[] {
		return (this.isArgDialog ? this.argDefs() : this.varDefs()).map((d) => d.key);
	}

	get defDialogKeyExists(): boolean {
		if (!this.isCreateDialog) return false;
		return !!findExistingKey(this.defDialogKey(), this.siblingKeys);
	}

	/**
	 * DEV-651: the naming rule blocks a key being **created**, and a key the user
	 * has deliberately changed while editing. An untouched legacy key is only
	 * warned about (`defDialogKeyWarning`) — a rename is not something this
	 * dialog can carry out safely, and blocking the save would trap the user out
	 * of editing the description of a def they cannot rename.
	 */
	get defDialogKeyError(): string | null {
		const editing = this.defDialogEditingKey();
		if (editing !== null && this.defDialogKey().trim() === editing) return null;
		return argKeyError(this.defDialogKey(), this.isCreateDialog ? this.siblingKeys : undefined);
	}

	/** Advisory only: this existing key predates the naming rule. */
	get defDialogKeyWarning(): string | null {
		const editing = this.defDialogEditingKey();
		if (editing === null || this.defDialogKey().trim() !== editing) return null;
		return isLegacyArgKey(editing)
			? 'This key predates the naming rule (lower case, no spaces). Renaming it would break embed codes already published — leave it unless you know every consumer.'
			: null;
	}

	get defDialogValid(): boolean {
		return this.defDialogKeyError === null;
	}

	// ── Arg def actions ─────────────────────────────────────────────────

	openNewArgDialog(): void {
		this.defDialogMode.set('create-arg');
		this.defDialogKey.set('');
		this.defDialogType.set('string');
		this.defDialogOptions.set('');
		this.defDialogDefaultValue.set('');
		this.defDialogDescription.set('');
		this.defDialogEditingKey.set(null);
		this.defDialogVisible.set(true);
	}

	openEditArgDialog(def: ArgDef): void {
		this.defDialogMode.set('edit-arg');
		this.defDialogKey.set(def.key);
		this.defDialogType.set(def.type);
		this.defDialogOptions.set(def.options?.join(', ') ?? '');
		this.defDialogDefaultValue.set(def.defaultValue ?? '');
		this.defDialogDescription.set(def.description ?? '');
		this.defDialogEditingKey.set(def.key);
		this.defDialogVisible.set(true);
	}

	openDeleteArgDialog(def: ArgDef): void {
		this.deleteDialogKey.set(def.key);
		this.deleteDialogKind.set('arg');
		this.deleteDialogUsage.set(null);
		this.deleteDialogLoading.set(true);
		this.deleteDialogVisible.set(true);
		this.configService.getArgDefUsage(def.key).then((usage) => {
			this.deleteDialogUsage.set(usage);
			this.deleteDialogLoading.set(false);
		});
	}

	// ── Var def actions ─────────────────────────────────────────────────

	openNewVarDialog(): void {
		this.defDialogMode.set('create-var');
		this.defDialogKey.set('');
		this.defDialogType.set('string');
		this.defDialogOptions.set('');
		this.defDialogDefaultValue.set('');
		this.defDialogDescription.set('');
		this.defDialogEditingKey.set(null);
		this.defDialogVisible.set(true);
	}

	openEditVarDialog(def: VarDef): void {
		this.defDialogMode.set('edit-var');
		this.defDialogKey.set(def.key);
		this.defDialogType.set(def.type);
		this.defDialogOptions.set('');
		this.defDialogDefaultValue.set(def.defaultValue ?? '');
		this.defDialogDescription.set(def.description ?? '');
		this.defDialogEditingKey.set(def.key);
		this.defDialogVisible.set(true);
	}

	openDeleteVarDialog(def: VarDef): void {
		this.deleteDialogKey.set(def.key);
		this.deleteDialogKind.set('var');
		this.deleteDialogUsage.set(null);
		this.deleteDialogLoading.set(true);
		this.deleteDialogVisible.set(true);
		this.configService.getVarDefUsage(def.key).then((usage) => {
			this.deleteDialogUsage.set(usage);
			this.deleteDialogLoading.set(false);
		});
	}

	// ── Dialog confirm ──────────────────────────────────────────────────

	confirmDefDialog(): void {
		// An edited def keeps its stored key verbatim — legacy keys are never
		// rewritten (DEV-651). Only a newly typed key is normalised.
		const editing = this.defDialogEditingKey();
		const key = editing !== null && this.defDialogKey().trim() === editing
			? editing
			: normalizeArgKey(this.defDialogKey());
		if (!key || this.defDialogKeyError) return;

		if (this.isArgDialog) {
			const options = this.defDialogType() === 'string' && this.defDialogOptions().trim()
				? this.defDialogOptions().split(',').map((s) => s.trim()).filter(Boolean)
				: undefined;
			const def: ArgDef = {
				key,
				type: this.defDialogType() as ArgDef['type'],
				options,
				defaultValue: normalizeDefaultValue(this.defDialogDefaultValue()),
				description: this.defDialogDescription().trim() || undefined,
			};
			if (this.isCreateDialog) {
				this.configService.addArgDef(def);
			} else {
				const editingKey = this.defDialogEditingKey();
				if (editingKey) this.configService.updateArgDef(editingKey, def);
			}
		} else {
			const def: VarDef = {
				key,
				type: this.defDialogType() as VarDef['type'],
				defaultValue: normalizeDefaultValue(this.defDialogDefaultValue()),
				description: this.defDialogDescription().trim() || undefined,
			};
			if (this.isCreateDialog) {
				this.configService.addVarDef(def);
			} else {
				const editingKey = this.defDialogEditingKey();
				if (editingKey) this.configService.updateVarDef(editingKey, def);
			}
		}
		this.defDialogVisible.set(false);
	}

	confirmDelete(): void {
		const usage = this.deleteDialogUsage();
		if (!usage || usage.count > 0) return;
		if (this.deleteDialogKind() === 'arg') {
			this.configService.removeArgDef(this.deleteDialogKey());
		} else {
			this.configService.removeVarDef(this.deleteDialogKey());
		}
		this.deleteDialogVisible.set(false);
	}
}

/** A blank string means "no default"; a real `false` or `0` is one. */
function normalizeDefaultValue(value: string | number | boolean): string | number | boolean | undefined {
	if (typeof value === 'string') return value.trim() || undefined;
	return value;
}
